import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getSelectedOfferPageMultiRoomDay } from 'selectors/offerPage/multipleRoomBookingSelectors'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import TextButton from 'LuxKit/Button/TextButton'
import { getSelectedFixedFlashLengthOfStayOfferPageDay, getSelectedOfferPageDay, getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { getFirstAvailableDuration } from 'components/OfferPage/FlashHotel/flashHotelCalendarSkipUtils'
import styled from 'styled-components'
import eases from 'styles/tools/eases'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import Group from 'components/utils/Group'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { getFixedFlashFooterSelectedDay } from '../CalendarPicker/fixedFlashLengthOfStayCalendarUtils'

const FooterContents = styled.div`
  position: absolute;
  bottom: 0;
  inset-inline: 0;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: unset;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

const MobileButtonGroup = styled(Group)`
  & > * {
    flex: 1 1 0;
    min-width: 0;
  }
`

interface Props {
  selectedDay?: App.CalendarDay
  fixedFlashLengthOfStaySelectedDay?: App.CalendarDay
  checkIn?: string
  checkOut?: string
  isFixedFlashLengthOfStayEnabled?: boolean
  minimumDuration?: number
  onApply: () => void
}

function OfferCalendarConfirmDatesFooter(props: Props) {
  const {
    selectedDay,
    fixedFlashLengthOfStaySelectedDay,
    checkIn,
    checkOut,
    isFixedFlashLengthOfStayEnabled = false,
    minimumDuration,
    onApply,
  } = props
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()

  const footerSelectedDay = useMemo(
    () => getFixedFlashFooterSelectedDay({
      isFixedFlashLengthOfStayEnabled,
      checkIn,
      checkOut,
      selectedDay,
      fixedFlashLengthOfStaySelectedDay,
    }),
    [checkIn, checkOut, fixedFlashLengthOfStaySelectedDay, isFixedFlashLengthOfStayEnabled, selectedDay],
  )

  const [localDay, setLocalDay] = useState<App.CalendarDay | undefined>(footerSelectedDay)
  const [initialDay, setInitialDay] = useState<App.CalendarDay | undefined>(footerSelectedDay)

  const hasSelectionChanged = useMemo(
    () => initialDay?.checkIn !== localDay?.checkIn || initialDay?.checkOut !== localDay?.checkOut,
    [initialDay, localDay],
  )

  const showClearDates = isFixedFlashLengthOfStayEnabled && !!checkIn
  const showConfirmNewDates = isFixedFlashLengthOfStayEnabled ?
      !!checkIn && !!checkOut :
      !!footerSelectedDay && hasSelectionChanged
  const isFooterVisible = isFixedFlashLengthOfStayEnabled ?
      !!checkIn :
      !!footerSelectedDay && hasSelectionChanged

  const clearDatesLabel = intl.formatMessage({
    id: 'RT3Wp6Ylio7DP0kOaccf',
    defaultMessage: 'Clear dates',
  })

  const confirmNewDatesLabel = intl.formatMessage({
    id: 'cueCwJlbpCoRHZw9jdp5',
    defaultMessage: 'Confirm new dates',
  })

  const onClearDates = useCallback(() => {
    if (isFixedFlashLengthOfStayEnabled && minimumDuration !== undefined) {
      dispatch({ type: OfferPageActions.resetDatesToMinimumDuration, minimumDuration })
      return
    }

    dispatch({ type: OfferPageActions.resetDates })
  }, [dispatch, isFixedFlashLengthOfStayEnabled, minimumDuration])

  useEffect(() => {
    if (footerSelectedDay) {
      setLocalDay(footerSelectedDay)
    }
  }, [footerSelectedDay])

  const onFooterClose = useCallback<React.TransitionEventHandler<HTMLDivElement>>((e) => {
    if (!isFooterVisible && e.propertyName === 'transition') {
      setLocalDay(undefined)
      setInitialDay(undefined)
    }
  }, [isFooterVisible])

  return <FooterContents
    className={clsx({ visible: isFooterVisible })}
    onTransitionEnd={onFooterClose}
  >
    <ModalFooter>
      {showClearDates && !showConfirmNewDates && <TextButton
        kind="secondary"
        size="large"
        fit="full-width"
        onClick={onClearDates}
      >
        {clearDatesLabel}
      </TextButton>}
      {showClearDates && showConfirmNewDates && <MobileButtonGroup direction="horizontal" gap={8}>
        <TextButton
          kind="secondary"
          size="large"
          fit="flex"
          onClick={onClearDates}
        >
          {clearDatesLabel}
        </TextButton>
        <TextButton
          kind="primary"
          size="large"
          fit="flex"
          type="submit"
          onClick={onApply}
        >
          {confirmNewDatesLabel}
        </TextButton>
      </MobileButtonGroup>}
      {!showClearDates && showConfirmNewDates && <TextButton
        kind="primary"
        size="large"
        fit="full-width"
        type="submit"
        onClick={onApply}
      >
        {confirmNewDatesLabel}
      </TextButton>}
    </ModalFooter>
  </FooterContents>
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
    fixedFlashLengthOfStaySelectedDay: isMultiRoom ? undefined : getSelectedFixedFlashLengthOfStayOfferPageDay(appState, state),
    checkIn: state.checkInDate,
    checkOut: state.checkOutDate,
    minimumDuration: getFirstAvailableDuration(getDurationOptions(state, appState)),
  }
}, OfferCalendarConfirmDatesFooter)
